import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ErrorRelationState } from '../../api/api';
import { Dossier, DossierMoment, DossierNode, ROLE_LABEL } from '../../api/dossier';
import { IdText } from './IdText';
import { stamp } from './money';
import './Money.scss';

/** Object types that travel from Exact to Chargebee; everything else goes the other way. */
const TOWARDS_CHARGEBEE = ['Payment', 'PaymentSalesEntry', 'Chargeback'];

const STATE: Partial<Record<ErrorRelationState, { label: string; tone: 'done' | 'open' | 'unknown' }>> = {
    [ErrorRelationState.InExact]: { label: 'in Exact', tone: 'done' },
    [ErrorRelationState.NotInExact]: { label: 'niet in Exact', tone: 'open' },
    [ErrorRelationState.Skipped]: { label: 'bewust overgeslagen', tone: 'unknown' },
    [ErrorRelationState.RemovedByChargeback]: { label: 'verwijderd na stornering', tone: 'unknown' },
    [ErrorRelationState.ChargebackProcessed]: { label: 'stornering verwerkt', tone: 'done' },
};

const label = (node?: DossierNode) => (node ? `${ROLE_LABEL[node.role] ?? node.role} ${node.relationId}` : '');

export interface IObjectFlowProps {
    dossier: Dossier;
    /** The object the screen is about; its moments are drawn heavier and its state closes the flow. */
    focusKey?: string | null;
    /** The most moments to show, the latest ones. */
    limit?: number;
}

/**
 * What happened to the objects of a dossier, in the three lanes of the money flow: what reached Exact on the right,
 * what came back to Chargebee on the left, and in between what the connector did and where it failed. For objects
 * without an invoice to settle, such as a customer, and for an invoice before anything was paid.
 */
export const ObjectFlow = ({ dossier, focusKey, limit = 14 }: IObjectFlowProps) => {
    const nodes = new Map(dossier.nodes.map((node) => [node.key, node]));
    const moments = dossier.moments
        .filter((m) => m.kind !== 'Note')
        .sort((a, b) => new Date(a.firstAt ?? 0).getTime() - new Date(b.firstAt ?? 0).getTime())
        .slice(-limit);
    const focus = focusKey ? nodes.get(focusKey) : undefined;
    const state = focus ? STATE[focus.exactState] : undefined;

    if (moments.length === 0) {
        return <p className="flow__empty">Geen gebeurtenissen in de logboeken.</p>;
    }

    return (
        <div className="flow" role="table" aria-label="Verloop in Chargebee, de connector en Exact">
            <div className="flow__row flow__row--head" role="row">
                <span role="columnheader">Wanneer</span>
                <span role="columnheader" className="flow__lane-head flow__lane-head--chargebee">
                    Chargebee
                </span>
                <span role="columnheader" className="flow__lane-head flow__lane-head--connector">
                    <FontAwesomeIcon icon="robot" /> Connector
                </span>
                <span role="columnheader" className="flow__lane-head flow__lane-head--exact">
                    Exact
                </span>
            </div>

            {moments.map((moment) => (
                <MomentRow key={moment.id} moment={moment} node={nodes.get(moment.nodeKey)} focus={moment.nodeKey === focusKey} />
            ))}

            {focus && state && (
                <div className="flow__row flow__row--now" role="row">
                    <span className="flow__when" role="cell">
                        Nu
                    </span>
                    <span role="cell" className="flow__cell flow__cell--chargebee" />
                    <span role="cell" className="flow__cell flow__cell--connector" />
                    <span role="cell" className="flow__cell flow__cell--exact">
                        <span className={`flow__now flow__now--${state.tone}`}>
                            <FontAwesomeIcon icon={state.tone === 'done' ? 'circle-check' : state.tone === 'open' ? 'circle-exclamation' : 'circle-info'} />{' '}
                            {ROLE_LABEL[focus.role] ?? focus.role} {state.label}
                        </span>
                    </span>
                </div>
            )}
        </div>
    );
};

const MomentRow = ({ moment, node, focus }: { moment: DossierMoment; node?: DossierNode; focus: boolean }) => {
    const title = moment.interpretation?.title ?? moment.headline;
    const when = (
        <span className="flow__when" role="cell">
            {stamp(moment.firstAt)}
        </span>
    );

    // A failure, or the connector offering something again: across the lanes, because it stopped the way over.
    if (moment.kind === 'Failed' || moment.kind === 'Requeued') {
        const failed = moment.kind === 'Failed';

        return (
            <div className={`flow__row flow__row--failure${focus ? ' flow__row--focus' : ''}`} role="row">
                {when}
                <span role="cell" className="flow__cell flow__cell--span">
                    <span className={failed ? 'flow__failure' : 'flow__retry'}>
                        <span className="flow__card-head">
                            <FontAwesomeIcon icon={failed ? 'circle-xmark' : 'rotate-left'} className="flow__card-icon" />
                            <span className="flow__card-title">
                                <IdText text={failed ? title : 'Opnieuw aangeboden'} />
                            </span>
                        </span>
                        <span className="flow__meta">
                            <IdText text={label(node)} />
                            {moment.count > 1 ? ` · ${moment.count} keer, laatste ${stamp(moment.lastAt)}` : ''}
                        </span>
                    </span>
                </span>
            </div>
        );
    }

    const back = !!node && TOWARDS_CHARGEBEE.includes(node.objectType);
    const card = (
        <div className={`flow__card${focus ? ' flow__card--focus' : ''}${moment.kind === 'Removed' ? ' flow__card--undone' : ''}`}>
            <span className="flow__card-head">
                <FontAwesomeIcon icon={moment.kind === 'Removed' ? 'ban' : 'circle-check'} className="flow__card-icon" />
                <span className="flow__card-title">
                    <IdText text={title} />
                </span>
            </span>
            <span className="flow__meta">
                <IdText text={label(node)} />
            </span>
        </div>
    );

    return (
        <div className={`flow__row${focus ? ' flow__row--focus' : ''}`} role="row">
            {when}
            <span role="cell" className="flow__cell flow__cell--chargebee">
                {back && card}
            </span>
            <span role="cell" className="flow__cell flow__cell--connector">
                {moment.kind === 'Succeeded' && (
                    <span className={`flow__carry flow__carry--${back ? 'back' : 'over'}`}>
                        {back ? (
                            <>
                                <FontAwesomeIcon icon="arrow-left-long" /> uit Exact
                            </>
                        ) : (
                            <>
                                naar Exact <FontAwesomeIcon icon="arrow-right-long" />
                            </>
                        )}
                    </span>
                )}
            </span>
            <span role="cell" className="flow__cell flow__cell--exact">
                {!back && card}
            </span>
        </div>
    );
};
